import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Badge, Box, Button, Card, Chip, Collapse, Divider, IconButton, Stack, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getStatus, TRANSLATION_MAP } from "../utils/reservation";
import { useWindowSize } from "../hooks/useWindowSize";
import { useGetWorkshopByIdQuery } from "../../app/store/api/workshopApi";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import BuildIcon from "@mui/icons-material/Build";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PersonIcon from "@mui/icons-material/Person";
import StarsIcon from "@mui/icons-material/Stars";
import SettingsIcon from "@mui/icons-material/Settings";
import ReservationConfirmDialog from "./ReservationConfirmDialog";
import dayjs from "dayjs";
import { useUpdateReservationSeenStatusMutation, useUpdateReservationStatusMutation } from "../../app/store/api/reservationApi";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";

const STATUS = {
  New: { label: "New", color: "#B45309", bg: "#FEF3E2", bar: "#F2A93B" },
  Pending: { label: "Pending", color: "#11a3e7ff", bg: "#bafcfcff", bar: "#33dbf8ff" },
  Confirmed: { label: "Confirmed", color: "#1D4ED8", bg: "#E8F0FE", bar: "#94f36fff" },
  Completed: { label: "Completed", color: "#15803D", bg: "#E7F6EC", bar: "#22C55E" },
  Canceled: { label: "Canceled", color: "#B91C1C", bg: "#FDEAEA", bar: "#EF4444" },
  Delivered: { label: "Delivered", color: "#334155", bg: "#EEF1F5", bar: "#64748B" },
};

const COLOR = "#003C59";
const TEXT = "#1A2233";
const BORDER = "#EBEDF1";
const PANEL_BG = "#F5F5F5";
const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 };
const wrap = { minWidth: 0, overflowWrap: "anywhere" };

const StatCell = ({ icon, label, children, divider = true }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
      pl: divider ? 2 : 0,
      borderLeft: divider ? `1px solid ${BORDER}` : "none",
      alignSelf: "stretch",
    }}
  >
    <Box sx={{ display: "flex", color: COLOR, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary", ...ellipsis }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, color: COLOR, fontWeight: 600, ...ellipsis }}>{children}</Typography>
    </Box>
  </Box>
);

const Panel = ({ icon, title, children, sx }) => (
  <Card variant='outlined' sx={{ borderRadius: 2, minWidth: 0, height: "100%", ...sx }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, bgcolor: PANEL_BG, color: COLOR }}>
      {icon}
      <Typography sx={{ fontWeight: 600, color: COLOR }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1 }}>{children}</Box>
  </Card>
);

const InfoTable = ({ rows, fontSize = 13 }) => (
  <Table size='small' sx={{ tableLayout: "fixed" }}>
    <TableBody>
      {rows.map(([label, value]) => (
        <TableRow key={label} sx={{ "&:last-child td": { borderBottom: 0 } }}>
          <TableCell sx={{ width: "38%", px: 1, verticalAlign: "top" }}>
            <Typography sx={{ color: "text.secondary", fontWeight: 600, fontSize }}>{label}</Typography>
          </TableCell>
          <TableCell sx={{ px: 1, wordBreak: "break-word" }}>
            <Typography sx={{ color: COLOR, fontSize }}>{value ?? "-"}</Typography>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const MobileRow = ({ label, children }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, py: 0.75 }}>
    <Typography sx={{ color: "text.secondary", fontWeight: 600, fontSize: 13, ...wrap }}>{label}</Typography>
    <Typography sx={{ color: COLOR, fontSize: 13, textAlign: "right", flexShrink: 0, maxWidth: "60%", ...wrap }}>{children ?? "-"}</Typography>
  </Box>
);

const MobileSection = ({ icon, title, children }) => (
  <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden", minWidth: 0 }}>
    <Stack direction='row' spacing={1} alignItems='center' sx={{ bgcolor: PANEL_BG, color: COLOR, px: 1.5, py: 1 }}>
      {icon}
      <Typography sx={{ fontWeight: 600, fontSize: 14, color: COLOR }}>{title}</Typography>
    </Stack>
    <Box sx={{ px: 1.5, py: 0.5 }}>{children}</Box>
  </Box>
);

const ReservationCard = ({ reservation, index, handleCardClick, expanded, adminView }) => {
  const { t, i18n } = useTranslation();
  const { isDesktop } = useWindowSize();
  const workshopId = reservation?.SK?.split("#")[1];
  const { data: workshopDetails } = useGetWorkshopByIdQuery(workshopId, { skip: !workshopId });
  const navigate = useNavigate();
  const location = useLocation();
  const status = STATUS[getStatus(reservation.status)];
  const price = reservation?.services?.reduce((total, service) => total + (service.price || 0), 0);
  const hasNote = !!reservation?.customer?.note?.trim();
  const info = reservation?.car?.basicinfo?.basicinfo;
  const fuel2 = info?.FuelType2 && info.FuelType2 !== "-" ? ` · ${info.FuelType2}` : "";
  const [openConfirm, setOpenConfirm] = useState(false);
  const [updateReservationStatus] = useUpdateReservationStatusMutation();
  const isConfirmed = status?.label === "Confirmed";
  const isCanceled = status?.label === "Canceled";

  const updateSeenStatus = async () => {
    await updateReservationStatus({
      reservationId: reservation?.PK,
      workshopId: reservation?.SK,
      newStatus: { canceled: false, confirmed: false, seen: true },
    });
  };

  const handleCardClickWrapper = async (e) => {
    e?.stopPropagation();
    handleCardClick(reservation?.PK);
    if (expanded && reservation?.status?.seen === false) await updateSeenStatus();
  };

  const handleConfirmOpen = async (e) => {
    e?.stopPropagation();
    setOpenConfirm(true);
  };

  const handleConfirmClose = async () => {
    setOpenConfirm(false);
    if (reservation?.status?.seen === false) await updateSeenStatus();
  };

  const handleOnConfirm = async () => {
    // Handle confirmation logic here
    const payload = {
      reservationId: reservation?.PK,
      workshopId: reservation?.SK,
      newStatus: { canceled: false, confirmed: true, seen: true },
    };
    toast.promise(updateReservationStatus(payload), {
      loading: t("toastMessage.confirmLoading"),
      success: t("toastMessage.confirmSuccess"),
      error: t("toastMessage.confirmError"),
    });
    setOpenConfirm(false);
    if (reservation?.status?.seen === false) await updateSeenStatus();
  };

  const handleWorkshopNameClick = (e) => {
    e?.stopPropagation();
    if (!workshopDetails?.id) return;
    navigate(`/admin/edit-workshop/${workshopDetails.id}`, { state: { from: location.pathname } });
  };

  const statusLabel = t(`userDashboard.${TRANSLATION_MAP[status.label]}`);
  const statusChip = <Chip label={statusLabel} size='small' sx={{ bgcolor: status.bg, color: status.color, fontWeight: 700, fontSize: 12, flexShrink: 0 }} />;

  const collapsedColumns = [
    "minmax(220px, 1.6fr)", // index + name + status
    "minmax(160px, 1.6fr)", // car
    ...(adminView ? ["minmax(120px, 1fr)"] : []), // workshop
    "minmax(130px, 1fr)", // date
    "minmax(80px, 0.6fr)", // total
    "minmax(80px, 0.6fr)", // confirm button
    "40px", // arrow
  ].join(" ");

  if (!isDesktop) {
    return (
      <Card
        elevation={0}
        onClick={(e) => handleCardClickWrapper(e)}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderRadius: 2,
          border: `1px solid ${BORDER}`,
          borderLeft: `4px solid ${status.bar}`,
          px: { xs: 2, sm: 2.5 },
          py: 2,
          cursor: "pointer",
          transition: "box-shadow .15s ease",
          "&:hover": { boxShadow: "0 2px 10px rgba(20,30,50,0.06)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Stack direction='row' spacing={1.5} alignItems='center' sx={{ minWidth: 0 }}>
            <Avatar sx={{ bgcolor: COLOR, width: 34, height: 34, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{index + 1}</Avatar>
            <Typography sx={{ fontWeight: 700, fontSize: expanded ? 17 : 15, color: TEXT, ...ellipsis }}>{reservation?.customer?.name}</Typography>
          </Stack>
          <Stack direction='row' spacing={0.5} alignItems='center' sx={{ flexShrink: 0 }}>
            {statusChip}
            <KeyboardArrowDownIcon
              sx={{
                color: "text.secondary",
                transition: "transform .2s ease",
                transform: expanded ? "rotate(180deg)" : "none",
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant='body2' sx={{ color: "text.secondary", ...wrap }}>
            {reservation?.car?.plate} · {info?.GenericName}
          </Typography>
          <Stack direction='row' spacing={1.5} alignItems='center' justifyContent='space-between' sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
            <Stack direction='row' spacing={0.5} alignItems='center'>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant='body2' sx={{ color: "text.secondary" }}>
                {dayjs(reservation?.date).format("D.M.YYYY")}
              </Typography>
            </Stack>
            <Typography variant='body2' sx={{ fontWeight: 700, color: TEXT }}>
              {price} &euro;
            </Typography>
          </Stack>
        </Box>

        <Collapse in={!!expanded} timeout='auto' unmountOnExit sx={{ width: "100%" }}>
          <Stack spacing={1.5}>
            <Divider />

            {adminView && (
              <Stack direction='row' spacing={1} alignItems='center' sx={{ bgcolor: PANEL_BG, borderRadius: 2, px: 1.5, py: 1, minWidth: 0 }}>
                <BuildIcon sx={{ fontSize: 16, color: COLOR, flexShrink: 0 }} />
                <Typography sx={{ color: COLOR, fontWeight: 600, fontSize: 14, flexShrink: 0 }}>{t("adminDashboard.workshop")}:</Typography>
                <Typography sx={{ color: COLOR, fontWeight: 600, fontSize: 14, ...ellipsis }} onClick={handleWorkshopNameClick}>
                  {workshopDetails?.name}
                </Typography>
              </Stack>
            )}

            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                alignItems: "start",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              }}
            >
              <MobileSection icon={<PersonIcon fontSize='small' />} title={t("adminDashboard.customerInfo")}>
                <MobileRow label={t("adminDashboard.name")}>{reservation?.customer?.name}</MobileRow>
                <MobileRow label={t("adminDashboard.email")}>{reservation?.customer?.email}</MobileRow>
                <MobileRow label={t("adminDashboard.phone")}>{reservation?.customer?.phone}</MobileRow>
              </MobileSection>

              <MobileSection icon={<DirectionsCarFilledIcon fontSize='small' />} title={t("adminDashboard.carInfo")}>
                <MobileRow label={t("adminDashboard.name")}>{info?.GenericName}</MobileRow>
                <MobileRow label={t("adminDashboard.plate")}>{reservation?.car?.plate}</MobileRow>
                <MobileRow label={t("adminDashboard.model")}>{info?.Model}</MobileRow>
                <MobileRow label={t("adminDashboard.bodyType")}>{info?.BodyType}</MobileRow>
                <MobileRow label={t("adminDashboard.dateOfRegister")}>{info?.DoR}</MobileRow>
                <MobileRow label={t("adminDashboard.fuelType")}>{`${info?.FuelType1 ?? "-"}${fuel2}`}</MobileRow>
              </MobileSection>

              {hasNote && (
                <MobileSection icon={<StarsIcon fontSize='small' />} title={t("adminDashboard.requestByUser")}>
                  <Typography variant='body2' sx={{ color: "text.secondary", py: 1, ...wrap }}>
                    {reservation?.customer?.note}
                  </Typography>
                </MobileSection>
              )}

              <MobileSection icon={<SettingsIcon fontSize='small' />} title={t("adminDashboard.services")}>
                {reservation?.services?.map((service, idx) => (
                  <MobileRow key={idx} label={service?.translations?.[i18n.language]?.name || service?.translations?.fi?.name}>
                    {service?.price} &euro;
                  </MobileRow>
                ))}
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, color: "text.secondary" }}>{t("adminDashboard.total")}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLOR }}>{price} &euro;</Typography>
                </Box>
              </MobileSection>
            </Box>
          </Stack>
        </Collapse>
      </Card>
    );
  }

  return (
    <>
      <Card
        elevation={0}
        onClick={(e) => handleCardClickWrapper(e)}
        sx={{
          width: "100%",
          boxSizing: "border-box",
          borderRadius: 2,
          border: `1px solid ${BORDER}`,
          borderLeft: `4px solid ${status.bar}`,
          px: 2.5,
          py: 2,
          cursor: "pointer",
          transition: "box-shadow .15s ease",
          "&:hover": { boxShadow: "0 2px 10px rgba(20,30,50,0.06)" },
        }}
      >
        {expanded ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
                <Avatar sx={{ bgcolor: COLOR, width: 40, height: 40, fontSize: 18, fontWeight: 700 }}>{index + 1}</Avatar>
                <Typography sx={{ fontWeight: 700, fontSize: 20, color: TEXT, ...ellipsis, maxWidth: 280 }}>{reservation?.customer?.name}</Typography>
                {statusChip}
                <Chip label={t(`${reservation?.PK?.split("-")[0].split("#")[1]}`)} size='small' sx={{ bgcolor: "#7CD5C7", color: COLOR, fontWeight: 700, fontSize: 12 }} />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <StatCell divider={false} icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />} label={t("adminDashboard.reservationDate")}>
                  {dayjs(reservation?.date).format("D.M.YYYY")}
                </StatCell>
                <Box sx={{ display: "flex", justifyContent: "center", alignSelf: "stretch", alignItems: "center" }}>
                  <Button
                    variant={isConfirmed && !isCanceled ? "outlined" : "contained"}
                    fullWidth
                    size='small'
                    onClick={handleConfirmOpen}
                    disabled={isCanceled && !isConfirmed}
                    sx={isConfirmed && !isCanceled ? {} : { bgcolor: "#00C9DB", color: "#003C59", fontWeight: 700, textTransform: "none", "&:hover": { bgcolor: "#00C9DB" } }}
                  >
                    {isConfirmed ? (isCanceled ? "Canceled from ERP" : t("common.view")) : t("common.confirm")}
                  </Button>
                </Box>
                <IconButton size='small'>
                  <KeyboardArrowUpIcon />
                </IconButton>
              </Box>
            </Box>

            {adminView && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: PANEL_BG, borderRadius: 2, px: 1.5, py: 1 }}>
                <BuildIcon sx={{ fontSize: 16, color: COLOR }} />
                <Typography sx={{ color: COLOR, fontWeight: 600 }}>Workshop:</Typography>
                <Typography sx={{ color: COLOR, fontWeight: 600, ...ellipsis }} onClick={handleWorkshopNameClick}>
                  {workshopDetails?.name}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                alignItems: "start",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              }}
            >
              <Panel icon={<PersonIcon />} title={t("adminDashboard.customerInfo")}>
                <InfoTable
                  fontSize={14}
                  rows={[
                    [t("adminDashboard.name"), reservation?.customer?.name],
                    [t("adminDashboard.email"), reservation?.customer?.email],
                    [t("adminDashboard.phone"), reservation?.customer?.phone],
                  ]}
                />
              </Panel>

              <Panel icon={<DirectionsCarFilledIcon />} title={t("adminDashboard.carInfo")}>
                <InfoTable
                  fontSize={12}
                  rows={[
                    [t("adminDashboard.name"), info?.GenericName],
                    [t("adminDashboard.plate"), reservation?.car?.plate],
                    [t("adminDashboard.model"), info?.Model],
                    [t("adminDashboard.bodyType"), info?.BodyType],
                    [t("adminDashboard.dateOfRegister"), info?.DoR],
                    [t("adminDashboard.fuelType"), `${info?.FuelType1 ?? ""}${fuel2}`],
                  ]}
                />
              </Panel>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}>
                {hasNote && (
                  <Panel icon={<StarsIcon />} title={t("adminDashboard.requestByUser")}>
                    <Typography variant='body2' sx={{ color: "text.secondary", p: 0.5, wordBreak: "break-word" }}>
                      {reservation?.customer?.note}
                    </Typography>
                  </Panel>
                )}

                <Panel icon={<SettingsIcon />} title={t("adminDashboard.services")}>
                  <Table size='small' sx={{ tableLayout: "fixed" }}>
                    <TableBody>
                      {reservation?.services?.map((service, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ width: "70%", px: 1 }}>
                            <Typography sx={{ color: "text.secondary", fontSize: 12, wordBreak: "break-word" }}>{service?.translations?.[i18n.language]?.name || service?.translations?.fi?.name}</Typography>
                          </TableCell>
                          <TableCell sx={{ width: "30%", px: 1 }}>
                            <Typography sx={{ color: "text.secondary", textAlign: "right", fontSize: 12 }}>{service?.price} &euro;</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ "& td": { borderBottom: 0 } }}>
                        <TableCell sx={{ px: 1 }}>
                          <Typography sx={{ color: "text.secondary", fontWeight: 600, fontSize: 16 }}>{t("adminDashboard.total")}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                          <Typography sx={{ color: COLOR, textAlign: "right", fontWeight: 600, fontSize: 16 }}>{price} &euro;</Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Panel>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: collapsedColumns,
              alignItems: "center",
              columnGap: 2,
              minWidth: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Avatar sx={{ bgcolor: COLOR, width: 34, height: 34, fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{index + 1}</Avatar>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: TEXT, ...ellipsis, flex: 1 }}>{reservation?.customer?.name}</Typography>
              {statusChip}
            </Box>

            <StatCell icon={<DirectionsCarFilledIcon sx={{ fontSize: 20 }} />} label={t("adminDashboard.carInfo")}>
              {reservation?.car?.plate} | {info?.GenericName}
            </StatCell>

            {adminView && (
              <StatCell icon={<BuildIcon sx={{ fontSize: 18 }} />} label={t("adminDashboard.workshop")}>
                <span onClick={handleWorkshopNameClick}>{workshopDetails?.name}</span>
              </StatCell>
            )}

            <StatCell icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />} label={t("adminDashboard.reservationDate")}>
              {dayjs(reservation?.date).format("D.M.YYYY")}
            </StatCell>

            <StatCell icon={<ReceiptLongIcon sx={{ fontSize: 20 }} />} label={t("adminDashboard.total")}>
              {price} &euro;
            </StatCell>

            <Box sx={{ display: "flex", justifyContent: "center", alignSelf: "stretch", alignItems: "center" }}>
              <Button
                variant={isConfirmed && !isCanceled ? "outlined" : "contained"}
                fullWidth
                size='small'
                onClick={handleConfirmOpen}
                disabled={isCanceled && !isConfirmed}
                sx={isConfirmed && !isCanceled ? {} : { bgcolor: "#00C9DB", color: "#003C59", fontWeight: 700, textTransform: "none", "&:hover": { bgcolor: "#00C9DB" } }}
              >
                {isConfirmed ? (isCanceled ? "Canceled from ERP" : t("common.view")) : t("common.confirm")}
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignSelf: "stretch", alignItems: "center" }}>
              <IconButton size='small'>
                <KeyboardArrowDownIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Card>
      <ReservationConfirmDialog open={openConfirm} onClose={handleConfirmClose} onConfirm={handleOnConfirm} reservation={{ ...reservation, workshopDetails }} />
    </>
  );
};

export default ReservationCard;
